/**
 * Slide data structure for Magic Forest Visuals
 * https://visuals.magicforest.land
 * 
 * @typedef {Object} SlideData
 * @property {string} [id] - Stable per-slide identifier (uuid). Used for drag tracking and per-slide tile color.
 * @property {number} [colorIndex] - Palette index (0..N-1) assigned at creation. Guarantees distinct tile colors across a carousel.
 * @property {string} kicker - Small text above title (e.g., "ABOUT", "ROADMAP")
 * @property {boolean} [kickerEnabled] - Whether kicker is visible
 * @property {string} title - Main slide title
 * @property {string} [subtitle] - Optional subtitle below title
 * @property {boolean} [subtitleEnabled] - Whether subtitle is visible
 * @property {string} body - Main body text
 * @property {boolean} [bodyEnabled] - Whether body is visible
 * @property {string[]} [meta] - Tags/badges array (max 6)
 * @property {boolean} [tagsEnabled] - Whether tags are visible on slide
 * @property {string[]} [paragraphs] - Additional paragraphs (max 4)
 * @property {string[]} [bullets] - Bullet points (max 5)
 * @property {boolean} [bulletsEnabled] - Whether bullets are visible
 * @property {number} [bgIndex] - Background image index (deprecated, use backgroundUrl)
 * @property {string} [backgroundUrl] - Custom background image URL
 * @property {number} [glassBlur] - Glass card blur intensity (0-5), default 1
 * @property {number} [fontPreset] - Font preset index (0=Classic/Inter, 1=Elegant/Raleway, 2=Modern/Montserrat), default 0
 * @property {string} [cta] - CTA text (bottom right)
 * @property {string} [ctaButton] - CTA button text (replaces cta text if set)
 * @property {Array<{label: string, value: string}>} [stats] - Stats widgets (max 4)
 * @property {{label: string, current: number, total: number}} [progress] - Progress bar
 * @property {{label: string, text: string}} [callout] - Highlighted callout/quote
 * @property {{label?: string, caption?: string, variant?: 'illustration' | 'photo', src?: string}} [imageWidget] - Image/media widget
 */

import { SLIDE_TILE_COLORS } from '../constants/slideTileColors';

/**
 * Generate a stable unique id for a slide. Uses crypto.randomUUID when
 * available; falls back to a random-hex string for older environments.
 * @returns {string}
 */
export function generateSlideId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) {
    /* fall through */
  }
  // Fallback — 128 bits of randomness as hex
  const rnd = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
  return `${rnd()}-${rnd()}-${rnd()}-${rnd()}`;
}

/**
 * Ensure every slide has a stable `id` AND a `colorIndex`. Idempotent.
 *
 * Color assignment is sequential to avoid birthday-paradox collisions:
 *   1. Collect color indices already in use by slides that have one.
 *   2. For each slide missing colorIndex, assign the lowest-numbered
 *      palette slot not yet in use. Once all 12 are taken, cycle.
 *
 * This yields distinct colors across a set of up to `palette.length`
 * slides, unlike pure hashing which routinely produces collisions.
 *
 * @param {SlideData[]} slides
 * @returns {SlideData[]}
 */
export function ensureSlideIds(slides) {
  if (!Array.isArray(slides) || slides.length === 0) return slides;
  const paletteSize = SLIDE_TILE_COLORS.length;

  // Colors already claimed by slides that have a colorIndex set
  const used = new Set(
    slides
      .filter((s) => s && typeof s === 'object' && typeof s.colorIndex === 'number')
      .map((s) => ((s.colorIndex % paletteSize) + paletteSize) % paletteSize)
  );
  // Queue of unused palette slots in canonical order
  const unused = [];
  for (let i = 0; i < paletteSize; i++) {
    if (!used.has(i)) unused.push(i);
  }
  let unusedPtr = 0;
  let cyclePtr = 0; // position counter for overflow past the palette

  let changed = false;
  const next = slides.map((s) => {
    if (!s || typeof s !== 'object') return s;
    let updated = s;
    if (!updated.id) {
      updated = { ...updated, id: generateSlideId() };
      changed = true;
    }
    if (typeof updated.colorIndex !== 'number') {
      const ci = unusedPtr < unused.length
        ? unused[unusedPtr++]
        : (cyclePtr++ % paletteSize);
      updated = { ...updated, colorIndex: ci };
      changed = true;
    }
    return updated;
  });
  return changed ? next : slides;
}

/**
 * Creates a default slide
 * @param {number} n - Slide index (0-based)
 * @param {number} total - Total number of slides
 * @param {number} bgCount - Number of available backgrounds
 * @returns {SlideData}
 */
export function createDefaultSlide(n, total, bgCount) {
  return {
    id: generateSlideId(),
    kicker: '',
    kickerEnabled: false,
    title: `SLIDE ${n + 1}`,
    subtitle: '',
    subtitleEnabled: false,
    body: 'Short body text goes here. Keep it compact for Instagram.',
    bodyEnabled: true,
    meta: [],
    tagsEnabled: false,
    paragraphs: [],
    bullets: [],
    bulletsEnabled: false,
    stats: [],
    progress: null,
    callout: null,
    imageWidget: null,
    videoWidget: null,
    footerEnabled: true,
    cta: n === 0 ? 'Swipe →' : `Slide ${n + 1} / ${total}`,
    ctaButton: 'magicforest.land',
    bgIndex: n % bgCount,
    backgroundUrl: null, // Will use default preset based on bgIndex
  };
}

/**
 * Updates slide CTA counters when slides are added/removed.
 * Only overwrites CTAs that match the "Slide X / N" numbering pattern.
 * Preserves "Swipe →", custom text, and any CTA with ctaButton set.
 * @param {SlideData[]} slides - Array of slides
 * @returns {SlideData[]}
 */
const NUMBERING_RE = /^Slide \d+ \/ \d+$/;

export function updateSlideCtas(slides) {
  return slides.map((s, i) => ({
    ...s,
    cta: !NUMBERING_RE.test(s.cta)
      ? s.cta
      : `Slide ${i + 1} / ${slides.length}`,
  }));
}
